// templateSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getUsageLimitMessage } from "../../../utils/usageLimitMessages";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Thunk to fetch templates
export const fetchTemplates = createAsyncThunk(
  "templates/fetchTemplates",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/templates/get`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data; // Extract templates array
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        const errorData = error.response.data;
        return rejectWithValue(
          errorData.message || "Invalid WhatsApp API credentials. Please update your workspace credentials."
        );
      }
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || error.message || "Failed to fetch templates"
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const status = getState()?.template?.status;
      if (status === "loading" || status === "succeeded") {
        return false;
      }
      return true;
    },
  }
);

/**
 * uploadFile
 * Uploads a single file to the server, returning a `link` or `media_id`.
 */
export const uploadFile = createAsyncThunk(
  "templates/uploadFile",
  async (file, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("No token found for upload");
      }

      const formData = new FormData();
      formData.append("file", file);
      // If your API expects a field name like `media` instead of `file`, adjust accordingly
      // formData.append("media", file);

      const response = await axios.post(
        `${SERVER_DOMAIN}/api/templates/sendupload`, // <-- Adjust to your API endpoint
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Suppose the API returns something like { success: true, link: "..."}
      // or { media_id: "..."}
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

/**
 * uploadFileWithMediaID
 * Uploads a single file (image/video) for carousel using the WhatsApp API to get a media ID
 */
export const uploadFileWithMediaID = createAsyncThunk(
  "templates/uploadFileWithMediaID",
  async (file, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("No token found for upload");
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${SERVER_DOMAIN}/api/templates/senduploadwithmediaid`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

/**
 * sendTemplateMessage
 * Sends the final template JSON to your backend for delivery.
 */
export const sendTemplateMessage = createAsyncThunk(
  "templates/sendTemplateMessage",
  async (payload, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("No token found for sending template.");
      }

      // POST the template JSON to your sending endpoint
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/templates/send`, // <--- Adjust to your endpoint
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Ensure we only return serializable data
      const responseData = response.data;
      
      // Return a plain object, never an axios response or error object
      if (responseData && typeof responseData === 'object') {
        return responseData;
      }
      return { success: true, message: 'Template sent successfully' };
    } catch (error) {
      console.error('Template send error:', error);
      
      // Always return plain objects, never error instances
      let errorData = {};
      
      if (error.response && error.response.data) {
        // Backend returned structured error data - extract only plain values
        const body = error.response.data;
        errorData = {
          ...body,
          message: getUsageLimitMessage(body),
          userFriendlyMessage: getUsageLimitMessage(body),
          errorCode: body.errorCode || body.code || "TEMPLATE_SEND_ERROR",
        };
      } else if (error.message) {
        errorData = {
          message: error.message,
          userFriendlyMessage: 'Network error occurred. Please check your connection and try again.',
          errorCode: 'NETWORK_ERROR'
        };
      } else {
        errorData = {
          message: 'Unknown error occurred',
          userFriendlyMessage: 'An unexpected error occurred. Please try again.',
          errorCode: 'UNKNOWN_ERROR'
        };
      }
      
      return rejectWithValue(errorData);
    }
  }
);

/**
 * deleteTemplate
 * Deletes a WhatsApp message template (Graph API + local tracking doc) by name.
 */
export const deleteTemplate = createAsyncThunk(
  "templates/deleteTemplate",
  async (name, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      await axios.delete(
        `${SERVER_DOMAIN}/api/templates/delete`,
        {
          params: { name },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return name;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.userFriendlyMessage ||
          error.response?.data?.message ||
          error.message ||
          "Failed to delete template"
      );
    }
  }
);

/**
 * fetchTemplateForEdit
 * Fetches a single template by Graph API id, with `example` values intact,
 * to prefill the edit dialog (the /get list strips examples for display).
 */
export const fetchTemplateForEdit = createAsyncThunk(
  "templates/fetchTemplateForEdit",
  async (templateGraphId, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/templates/edit/${templateGraphId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.userFriendlyMessage ||
          error.response?.data?.message ||
          error.message ||
          "Failed to load template for editing"
      );
    }
  }
);

/**
 * updateTemplate
 * Submits edited category/components for an existing template (Graph API
 * POST /{template-id} + local list update) by its Graph API id.
 */
export const updateTemplate = createAsyncThunk(
  "templates/updateTemplate",
  async ({ id, category, components }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      await axios.post(
        `${SERVER_DOMAIN}/api/templates/edit/${id}`,
        { category, components },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { id, category, components };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.userFriendlyMessage ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update template"
      );
    }
  }
);

const initialState = {
  activeStep: 0,
  steps: ["Set up template", "Edit template", "Submit for review"],
  selectedTemplateCategory: "Marketing", // Default category
  templates: [], // Store fetched templates
  status: "idle", // idle | loading | succeeded | failed
  error: null, // Store any fetch error
  selectedTemplate: null, // <--- new field
  // Upload-related state
  uploadStatus: "idle", // idle | loading | succeeded | failed
  uploadError: null,
  uploadResult: null, // will store { link: "...", media_id: "...", etc. } from the API
  // Send-related state
  sendStatus: "idle", // idle | loading | succeeded | failed
  sendError: null,
  uploadMediaIDStatus: "idle", // idle | loading | succeeded | failed
  uploadMediaIDError: null,
  uploadMediaIDResult: null, // will store { mediaId: "...", fileType: "..." } from the API
  // Delete-related state
  deleteStatus: "idle", // idle | loading | succeeded | failed
  deleteError: null,
  // Edit-related state
  editFetchStatus: "idle", // idle | loading | succeeded | failed
  editFetchError: null,
  editTemplateData: null, // full template (with examples) loaded for the edit dialog
  updateStatus: "idle", // idle | loading | succeeded | failed
  updateError: null,
};

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {
    updateSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },
    nextStep: (state) => {
      if (state.activeStep < state.steps.length - 1) {
        state.activeStep += 1;
      }
    },
    previousStep: (state) => {
      if (state.activeStep > 0) {
        state.activeStep -= 1;
      }
    },
    setTemplateCategory: (state, action) => {
      state.selectedTemplateCategory = action.payload;
    },
    // Clear upload state if user wants to reset between uploads
    clearUploadState: (state) => {
      state.uploadStatus = "idle";
      state.uploadError = null;
      state.uploadResult = null;
    },
    // Optional: clear send state if needed
    clearSendState: (state) => {
      state.sendStatus = "idle";
      state.sendError = null;
    },
    clearUploadMediaIDState: (state) => {
      state.uploadMediaIDStatus = "idle";
      state.uploadMediaIDError = null;
      state.uploadMediaIDResult = null;
    },
    resetActiveStep: (state) => {
      state.activeStep = 0;
    },
    // Jumps straight to a given wizard step — used to open the create wizard
    // at step 2 (body editor) when editing an existing template.
    setActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },
    clearDeleteState: (state) => {
      state.deleteStatus = "idle";
      state.deleteError = null;
    },
    clearEditState: (state) => {
      state.editFetchStatus = "idle";
      state.editFetchError = null;
      state.editTemplateData = null;
      state.updateStatus = "idle";
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.templates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Upload file
      .addCase(uploadFile.pending, (state) => {
        state.uploadStatus = "loading";
        state.uploadError = null;
        state.uploadResult = null;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.uploadStatus = "succeeded";
        state.uploadResult = action.payload; // e.g. { link: "...", media_id: "..." }
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.uploadStatus = "failed";
        state.uploadError = action.payload;
      })
      // uploadFileWithMediaID
      .addCase(uploadFileWithMediaID.pending, (state) => {
        state.uploadMediaIDStatus = "loading";
        state.uploadMediaIDError = null;
        state.uploadMediaIDResult = null;
      })
      .addCase(uploadFileWithMediaID.fulfilled, (state, action) => {
        state.uploadMediaIDStatus = "succeeded";
        state.uploadMediaIDResult = action.payload;
      })
      .addCase(uploadFileWithMediaID.rejected, (state, action) => {
        state.uploadMediaIDStatus = "failed";
        state.uploadMediaIDError = action.payload;
      })
      // sendTemplateMessage
      .addCase(sendTemplateMessage.pending, (state) => {
        state.sendStatus = "loading";
        state.sendError = null;
      })
      .addCase(sendTemplateMessage.fulfilled, (state, action) => {
        state.sendStatus = "succeeded";
        // If the backend returns success info, you can store it here if desired
      })
      .addCase(sendTemplateMessage.rejected, (state, action) => {
        state.sendStatus = "failed";
        state.sendError = action.payload;
      })
      // deleteTemplate
      .addCase(deleteTemplate.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.templates = state.templates.filter((t) => t.name !== action.payload);
        if (state.selectedTemplate?.name === action.payload) {
          state.selectedTemplate = null;
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.payload;
      })
      // fetchTemplateForEdit
      .addCase(fetchTemplateForEdit.pending, (state) => {
        state.editFetchStatus = "loading";
        state.editFetchError = null;
        state.editTemplateData = null;
      })
      .addCase(fetchTemplateForEdit.fulfilled, (state, action) => {
        state.editFetchStatus = "succeeded";
        state.editTemplateData = action.payload;
      })
      .addCase(fetchTemplateForEdit.rejected, (state, action) => {
        state.editFetchStatus = "failed";
        state.editFetchError = action.payload;
      })
      // updateTemplate
      .addCase(updateTemplate.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        const { id, category, components } = action.payload;
        // Keep the templates list (used elsewhere for previews/analytics) in sync
        // without a full refetch — strip examples same as the /get endpoint does.
        const stripExamples = (comp) => {
          const { example: _example, ...rest } = comp;
          if (rest.type === "BUTTONS" && Array.isArray(rest.buttons)) {
            rest.buttons = rest.buttons.map((btn) => {
              const { example: _btnExample, ...btnRest } = btn;
              return btnRest;
            });
          }
          return rest;
        };
        const strippedComponents = components.map(stripExamples);
        state.templates = state.templates.map((t) =>
          t.id === id ? { ...t, category, components: strippedComponents } : t
        );
        if (state.selectedTemplate?.id === id) {
          state.selectedTemplate = { ...state.selectedTemplate, category, components: strippedComponents };
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      });
  },
});

export const {
  nextStep,
  previousStep,
  setTemplateCategory,
  updateSelectedTemplate,
  clearUploadState,
  clearSendState,
  clearUploadMediaIDState,
  resetActiveStep,
  setActiveStep,
  clearDeleteState,
  clearEditState,
} = templateSlice.actions;

export default templateSlice.reducer;
